import {
  BikShimmer,
  BodyCaption,
  BodyRegular,
  BodySecondary,
  BodySmall,
  COLORS,
  CheckBox,
  Dropdown,
  DynamicTabs,
  Input,
  TitleRegular,
  TitleSmall,
} from "@bikdotai/bik-component-library";
import { useEffect, useState } from "react";
import {
  AiVoiceAgentBackendConfig,
  AiVoiceAgentFrontendConfig,
  VOICE_AGENT_OUTCOME,
  VOICE_AGENT_OUTCOMES,
  VOICE_AGENT_TYPE,
  VOICE_AGENT_TYPE_OPTIONS,
  DiscountConfig,
  PAYMENT_MODE,
  DISCOUNT_TYPE,
} from "./models";
import { useAppSelector } from "../../state/store";
import { nodeClickDataActions } from "../../state/nodeClickDataState";
import { storeDataActions } from "../../state/storeDataState";
import { useDispatch } from "react-redux";
import { NodeSubType } from "../../models/nodeSubType";
import { NodeType } from "../../models/nodeType";
import { DropdownOption, SingleOption } from "@bikdotai/bik-component-library/dist/esm/components/dropdown/type";
import { FirebaseService } from "../../services/firebase/FirebaseService";
import { NodeActionActions } from "../../state/nodeActionState";
import { aiVariablesDataAction } from "../../state/aiVariablesState";
import {
  ConfigSection,
  CurrencySymbol,
} from "./styles";

interface VoiceAgentConfig {
  variantConfigs?: {
    discountRules?: {
      canOffer?: boolean;
    };
    codPolicy?: {
      enabled?: boolean;
    };
  };
  [key: string]: any;
}

interface AIVoiceAgentProps {
  nodeData: any;
  saveTo: string;
  setSaveTo: any;
  config: any;
  storeId?: string;
}

const AIVoiceAgent = (props: AIVoiceAgentProps) => {
  const { saveTo, setSaveTo, nodeData, config } = props;
  const homeState = useAppSelector((state) => state.homeState);
  const [isLoading, setIsLoading] = useState(false);

  const [bodyContent, setBodyContent] = useState<{ [key: string]: string }>();
  const [selectedFields, setSelectedFields] = useState<{
    [key: string]: string[];
  }>();
  const [bodyText, setBodyText] = useState<{ [key: string]: string }>();
  const [paymentMode, setPaymentMode] = useState<string>(PAYMENT_MODE.PREPAID_AND_COD);
  const [agentType, setAgentType] = useState<string>(VOICE_AGENT_TYPE.CHECKOUT_RECOVERY_VOICE_AGENT);
  const [discountConfig, setDiscountConfig] = useState<DiscountConfig>({
    code: "",
    description: "",
    value: 0,
    codeType: DISCOUNT_TYPE.FIXED_AMOUNT,
  });

  // State for Firebase fetched config
  const [canOfferDiscount, setCanOfferDiscount] = useState<boolean>(false);
  const [codEnabled, setCodEnabled] = useState<boolean>(false);

  const dispatcher = useDispatch();
  const firebaseService = new FirebaseService(() => { }, () => { });

  // Fetch voice agent config from Firebase
  useEffect(() => {
    if (!homeState.storeId || !agentType) {
      return;
    }

    const fetchVoiceAgentConfig = async () => {
      setIsLoading(true);
      try {
        const config: VoiceAgentConfig = await firebaseService.getVoiceAgentConfig(
          homeState.storeId,
          agentType
        );

        if (config?.variantConfigs) {
          // Check if discount can be offered (controls visibility in UI)
          const canOffer = config.variantConfigs.discountRules?.canOffer || false;
          setCanOfferDiscount(canOffer);

          // Check COD policy
          // COD is enabled if the fee is configured, even when the charge is 0
          const codPolicy = config.variant?.config?.abandoned_cart?.paymentPlan?.additionalFee?.cod != null;
          setCodEnabled(codPolicy);
        }
      } catch (error) {
        console.error("Error fetching voice agent config:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVoiceAgentConfig();
  }, [homeState.storeId, agentType]);

  // Load node configuration
  useEffect(() => {
    if (!homeState.storeId) {
      return;
    }
    setBodyContent({
      bodyText: nodeData?.conf?.custom_prompt
        ? `<p>${nodeData?.conf?.custom_prompt}</p>`
        : "",
    });
    setBodyText({ bodyText: nodeData?.conf?.custom_prompt ?? "" });
    setPaymentMode(codEnabled ? PAYMENT_MODE.PREPAID_AND_COD : PAYMENT_MODE.PREPAID_ONLY);
    setAgentType(nodeData?.conf?.agent_type ?? VOICE_AGENT_TYPE.CHECKOUT_RECOVERY_VOICE_AGENT);
    setDiscountConfig(nodeData?.conf?.extraParams?.discountConfig ?? {
      code: "",
      description: "",
      value: 0,
      codeType: DISCOUNT_TYPE.FIXED_AMOUNT,
    });
  }, [homeState.storeId, codEnabled]);

  const generateOperatorValues = () => {
    const existingOperatorValues =
      nodeData?.conf?.conditions?.[0]?.operator_values ?? {};
    const operatorValues: any = {};
    const isCodMode = paymentMode === PAYMENT_MODE.PREPAID_AND_COD;

    // Outcomes only available when COD mode is active
    const codOnlyOutcomes = new Set([
      VOICE_AGENT_OUTCOME.COD_ORDER_PLACED,
      VOICE_AGENT_OUTCOME.COD_ADDR_UNCONFIRMED,
    ]);

    VOICE_AGENT_OUTCOMES.forEach((outcome) => {
      if (codOnlyOutcomes.has(outcome.id as VOICE_AGENT_OUTCOME) && !isCodMode) {
        return;
      }
      operatorValues[outcome.id] = {
        key: outcome.label,
        value: existingOperatorValues[outcome.id]?.value ?? "",
      };
    });

    return operatorValues;
  };
  // Delete COD-only edges if payment mode is not PREPAID_AND_COD
  const handleDeleteCodEdgeIfNeeded = () => {
    if (paymentMode === PAYMENT_MODE.PREPAID_AND_COD) {
      return;
    }
    const existingOperatorValues = nodeData?.conf?.conditions?.[0]?.operator_values ?? {};
    const codOnlyOutcomes = [
      VOICE_AGENT_OUTCOME.COD_ORDER_PLACED,
      VOICE_AGENT_OUTCOME.COD_ADDR_UNCONFIRMED,
    ];
    const edgeData = codOnlyOutcomes
      .filter((id) => existingOperatorValues?.[id]?.value)
      .map((id) => ({
        source: nodeData.nodeId,
        sourceHandle: id,
        target: existingOperatorValues[id].value,
      }));

    if (edgeData.length > 0) {
      dispatcher(
        NodeActionActions.updateState({
          type: "edge",
          action: "deleteMultiple",
          edgeData: edgeData,
          nodeId: "",
        })
      );
    }
  };
  useEffect(() => {
    if (saveTo === NodeSubType.AI_VOICE_AGENT) {

      const operatorValues = generateOperatorValues();

      // Define the variable payload structure
      const variablePayload = {
        draftOrder: {
          id: "string",
          name: "string",
          total: "number",
          subTotal: "number",
          shippingCharge: "number",
          totalTax: "number",
          discount: "number",
          currency: "string",
          status: "string",
          paymentStatus: "string",
          invoiceUrl: "string",
          createdAt: "string",
          expiresAt: "string",
          taxIncluded: "boolean",
          notes: "string",
          tags: "string",
          customerId: "string",
          phoneNumber: "string",
          email: "string",
          items: [
            {
              id: "string",
              title: "string",
              quantity: "number",
              price: "number",
              variantId: "string",
              productId: "string",
            },
          ],
          address: "object",
          billingAddress: "object",
          deliveryInfo: "object",
        },
        paymentMode: "string",
        whatsappReason: "string",
      };

      const nodeConf: AiVoiceAgentBackendConfig = {
        type: NodeType.AI_VOICE_AGENT,
        sub_type: nodeData.subtype,
        node_index: config.nodeIndex,
        agent_type: agentType,
        payment_mode: paymentMode,
        extraParams: {
          discountConfig: discountConfig,
        },
        conditions: [{ operator: "goto", operator_values: operatorValues }],
      };

      const feConf: AiVoiceAgentFrontendConfig = {
        type: NodeType.AI_VOICE_AGENT,
        subType: nodeData.subtype,
        isMicroFlow: false,
        description: "AI Voice Agent",
        childNodes: operatorValues,
      };

      handleDeleteCodEdgeIfNeeded();

      dispatcher(
        nodeClickDataActions.setEditNode({
          nodeId: nodeData.nodeId,
          nodeType: feConf.type,
          nodeSubType: feConf.subType,
          data: feConf,
        })
      );

      dispatcher(
        storeDataActions.updateInsertNode({
          flow: {},
          nodeId: nodeData.nodeId,
          data: nodeConf,
        })
      );
      dispatcher(
        aiVariablesDataAction.setAiVariablesList({
          aiVariablesList: {
            [nodeData.nodeId]: {
              name: `AI VOICE AGENT #${config.nodeIndex}`,
              value: `aiVoiceAgent${config.nodeIndex}.response`,
              type: "object",
              payload: variablePayload,
            },
          },
        })
      );

      setSaveTo("close");
    }
  }, [saveTo, paymentMode, bodyText, agentType, discountConfig]);

  if (isLoading) {
    return <BikShimmer boxes={[{ width: "100%", height: "400px" }]} />;
  }


  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <div>
          <TitleRegular>Setup your Voice AI agent</TitleRegular>
          <BodySecondary>Define the behavior of your Voice AI agent</BodySecondary>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <BodySecondary>Type of agent</BodySecondary>
          <Dropdown
            options={VOICE_AGENT_TYPE_OPTIONS.map(group => ({
              ...group,
              options: group.options.map(option => ({
                ...option,
                selected: option.value === agentType,
              })),
            }))}
            isMultiSelect={false}
            onSelect={(option: DropdownOption | DropdownOption[]) => {
              const selected = Array.isArray(option) ? option[0] : option;
              if (selected && (selected as SingleOption).value) {
                setAgentType((selected as SingleOption).value);
              }
            }}
            placeHolder="Select agent type"
          />
        </div>
        <BodyCaption style={{ marginTop: "16px" }}>
          To learn more about AI Voice Agents,{" "}
          <span
            style={{ color: COLORS.content.brand, cursor: "pointer" }}
            onClick={() => window.open("mailto:help@bik.ai", "_blank")}
          >
            contact support.
          </span>
        </BodyCaption>
      </div>
      {(
        <ConfigSection onClick={(e) => e.stopPropagation()}>
          <TitleSmall style={{ marginBottom: 4 }}>Share payment link</TitleSmall>
          <BodySecondary style={{ marginBottom: 16 }}>Choose a payment mode to share the payment link</BodySecondary>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {codEnabled && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckBox
                  isChecked={true}
                  onValueChange={() => {}}
                  isDisabled={true}
                />
                <BodySecondary style={{ color: COLORS.content.primary }}>
                  COD
                </BodySecondary>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckBox
                isChecked={true}
                onValueChange={() => {}}
                isDisabled={true}
              />
              <BodySecondary style={{ color: COLORS.content.primary }}>
                Prepaid / Online
              </BodySecondary>
            </div>
          </div>
        </ConfigSection>
      )}
      {canOfferDiscount && (
        <ConfigSection>
          <TitleSmall>Configure discount</TitleSmall>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 16, marginTop: 4 }}>
            <BodyCaption style={{ color: COLORS.content.secondary }}>
              Add a discount to drive more purchases
            </BodyCaption>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Input
                labelText="Discount code"
                value={discountConfig.code}
                maxCharLimit={60}
                onChangeText={(value: string) => {
                  setDiscountConfig({ ...discountConfig, code: value });
                }}
                placeholder="eg: FLAT50"
              />
              <BodyCaption style={{ color: COLORS.content.secondary }}>
                Code created on Shopify
              </BodyCaption>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Input
                labelText="Coupon description"
                value={discountConfig.description}
                maxCharLimit={60}
                onChangeText={(value: string) => {
                  setDiscountConfig({ ...discountConfig, description: value });
                }}
                placeholder='eg: Flat ₹50 off'
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <TitleSmall>Discount type</TitleSmall>
              <DynamicTabs
                tabsInfo={[
                  {
                    id: 1,
                    text: "₹  Fixed amount",
                    action: () => setDiscountConfig({ ...discountConfig, codeType: DISCOUNT_TYPE.FIXED_AMOUNT, value: 0 }),
                  },
                  {
                    id: 2,
                    text: "%  Percentage",
                    action: () => setDiscountConfig({ ...discountConfig, codeType: DISCOUNT_TYPE.PERCENTAGE, value: 0 }),
                  },
                ]}
                tabStyleInfo={{
                  type: "SQUARE",
                  size: "REGULAR",
                }}
                selectedTabId={discountConfig.codeType === DISCOUNT_TYPE.FIXED_AMOUNT ? 1 : 2}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                {discountConfig.codeType === DISCOUNT_TYPE.FIXED_AMOUNT && (
                  <CurrencySymbol position="left">₹</CurrencySymbol>
                )}
                {discountConfig.codeType === DISCOUNT_TYPE.PERCENTAGE && (
                  <CurrencySymbol position="right">%</CurrencySymbol>
                )}
                <div style={{ width: "100%" }}>
                  <Input
                    labelText={discountConfig.codeType === DISCOUNT_TYPE.FIXED_AMOUNT ? "Discount amount" : "Discount percentage"}
                    value={discountConfig.value.toString()}
                    onChangeText={(value: string) => {
                      // Only allow numbers
                      if (/^\d*$/.test(value)) {
                        setDiscountConfig({ ...discountConfig, value: value === "" ? 0 : parseInt(value, 10) });
                      }
                    }}
                    placeholder={discountConfig.codeType === DISCOUNT_TYPE.FIXED_AMOUNT ? "50" : "50"}
                    inputWrapperStyles={{
                      paddingLeft: discountConfig.codeType === DISCOUNT_TYPE.FIXED_AMOUNT ? "40px" : "16px",
                      paddingRight: discountConfig.codeType === DISCOUNT_TYPE.PERCENTAGE ? "40px" : "16px",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </ConfigSection>
      )}
    </div>
  );
};

export default AIVoiceAgent;
